import React from 'react';

export type WorkshopVariant = 'produktion' | 'verwaltung';

export interface WorkshopEvent {
  id: string;
  variant: WorkshopVariant;
  partnerName?: string;
  description?: string;
  descriptionSmall?: boolean;
  /** Value that maps to the region <select> in KIWerkstattRegistrationForm */
  formRegion?: string;
  nextDate?: string;
  price?: string;
  priceSuffix?: string;
  feeInfo?: string;
  logos?: { src: string; alt: string; url?: string }[];
}

export interface Location {
  id: string;
  name: string;
  /** X coordinate within the Ger.svg viewBox (0 0 591.504 800.504) */
  x: number;
  /** Y coordinate within the Ger.svg viewBox (0 0 591.504 800.504) */
  y: number;
  events: WorkshopEvent[];
}

interface GermanyMapProps {
  locations: Location[];
  activeLocationId: string | null;
  onLocationInteract: (locationId: string) => void;
}

const GermanyMap: React.FC<GermanyMapProps> = ({ locations, activeLocationId, onLocationInteract }) => {
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg
        viewBox="0 0 591.504 800.504"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        {/* Germany map rendered from Ger.svg asset */}
        <image
          href="/assets/Ger.svg"
          x="0"
          y="0"
          width="591.504"
          height="800.504"
        />

        {/* Location markers */}
        {locations.map((location) => {
          const isActive = activeLocationId === location.id;
          return (
            <g
              key={location.id}
              onMouseEnter={() => onLocationInteract(location.id)}
              onClick={() => onLocationInteract(location.id)}
              style={{ cursor: 'pointer' }}
            >
              {/* Pulsating outer ring (inactive) */}
              {!isActive && (
                <circle
                  cx={location.x}
                  cy={location.y}
                  r="16"
                  fill="none"
                  stroke="#008F99"
                  strokeWidth="2"
                  opacity="0.6"
                >
                  <animate
                    attributeName="r"
                    from="11"
                    to="21"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    from="0.8"
                    to="0"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}

              {/* Static ring (active) */}
              {isActive && (
                <circle
                  cx={location.x}
                  cy={location.y}
                  r="16"
                  fill="none"
                  stroke="#008F99"
                  strokeWidth="3"
                  opacity="0.8"
                  style={{ transition: 'all 0.3s ease' }}
                />
              )}

              {/* Main dot */}
              <circle
                cx={location.x}
                cy={location.y}
                r={isActive ? 10 : 8}
                fill="#008F99"
                stroke="#fff"
                strokeWidth="2"
                style={{
                  transition: 'all 0.3s ease',
                  filter: isActive
                    ? 'drop-shadow(0 4px 8px rgba(0, 143, 153, 0.6))'
                    : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                }}
              />

              {/* Inner highlight dot */}
              <circle
                cx={location.x}
                cy={location.y}
                r={isActive ? 5 : 4}
                fill="#fff"
                opacity="0.8"
                style={{ transition: 'all 0.3s ease' }}
              />

              {/* City label on active */}
              {isActive && (
                <text
                  x={location.x}
                  y={location.y + 26}
                  textAnchor="middle"
                  fill="#008F99"
                  fontSize="14"
                  fontWeight="600"
                  style={{ pointerEvents: 'none' }}
                >
                  {location.name}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default GermanyMap;
